import { StyledTableLink } from '@common/Dependency/styled';
import { Entity } from '@common/types/Entity';
import { getSqlIDEUrl } from '@common/url';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Text } from '@prophecy/ui-v3/Typography';
import { SqlPipelineRunType } from 'frontend/core/src/data/apis/types';
import styled from 'styled-components';

import { statusBadges } from '../Components/StatusBadge';
import { StyledMoreDetailsText } from './styled';
import { PipelineRunRecord, SQLPipelineRunStatus } from './types';

const StyledTooltipContainer = styled(Stack)`
  max-width: 400px;
`;

export function withEllipsis(text: string) {
  return (
    <Ellipsis tooltip>
      <Text level='sm'>{text}</Text>
    </Ellipsis>
  );
}

const StyledEllipsisLink = styled(StyledTableLink)`
  max-width: 100%;
`;

export function renderProjectLink({
  projectId,
  projectsMap,
  dataTestId
}: {
  projectId: string;
  projectsMap: Map<string, string>;
  dataTestId: string;
}) {
  const projectName = projectsMap.get(projectId);
  if (!projectName) {
    return withEllipsis(projectId);
  }
  return (
    <StyledEllipsisLink to={getSqlIDEUrl({ uid: projectId })} data-test-id={dataTestId}>
      <Ellipsis tooltip>{projectName}</Ellipsis>
    </StyledEllipsisLink>
  );
}

export function renderPipelineLink({
  projectId,
  pipelineName,
  projectsMap,
  dataTestId
}: {
  projectId: string;
  pipelineName: string;
  projectsMap: Map<string, string>;
  dataTestId: string;
}) {
  if (!projectsMap.has(projectId)) {
    return withEllipsis(pipelineName);
  }
  return (
    <StyledEllipsisLink
      to={getSqlIDEUrl({ uid: projectId, name: pipelineName, entity: Entity.Pipeline.toLowerCase() })}
      data-test-id={dataTestId}>
      <Ellipsis tooltip>{pipelineName}</Ellipsis>
    </StyledEllipsisLink>
  );
}

function StatusTooltip({ message, onExpandClick }: { message: string; onExpandClick?: () => void }) {
  return (
    <StyledTooltipContainer gap={theme.spaces.x6}>
      <Ellipsis>
        <Text level='sm'>{message}</Text>
      </Ellipsis>
      <Button variant='plain' onClick={onExpandClick} data-test-id='sql-pipeline-utils-error-details-button'>
        <StyledMoreDetailsText level='sm' weight={theme.fontWeight.bold}>
          More Details
        </StyledMoreDetailsText>
      </Button>
    </StyledTooltipContainer>
  );
}

const statusBadgeByRunStatus = {
  [SQLPipelineRunStatus.RUNNING]: statusBadges.Running,
  [SQLPipelineRunStatus.CANCELLING]: statusBadges.Cancelling,
  [SQLPipelineRunStatus.SUCCEEDED]: statusBadges.Success,
  [SQLPipelineRunStatus.ERROR]: statusBadges.Failed,
  [SQLPipelineRunStatus.WARNING]: statusBadges.Warning,
  [SQLPipelineRunStatus.CANCELLED]: statusBadges.Cancelled,
  [SQLPipelineRunStatus.QUEUED]: statusBadges.Queued,
  [SQLPipelineRunStatus.SKIPPED]: statusBadges.Skipped
};

export function getRunStatusBadge(runStatus: string, message?: string, onExpandMessageClick?: () => void) {
  const StatusBadge = statusBadgeByRunStatus[runStatus as SQLPipelineRunStatus];
  if (!StatusBadge) return null;

  const showDetails =
    Boolean(message) &&
    (runStatus === SQLPipelineRunStatus.ERROR ||
      runStatus === SQLPipelineRunStatus.WARNING ||
      runStatus === SQLPipelineRunStatus.CANCELLED);

  return (
    <StatusBadge
      tooltip={showDetails ? <StatusTooltip message={message || ''} onExpandClick={onExpandMessageClick} /> : undefined}
    />
  );
}

export const isRunInFlight = (runStatus: string) =>
  runStatus === SQLPipelineRunStatus.RUNNING || runStatus === SQLPipelineRunStatus.CANCELLING;

export function getDisplayRunType(run: PipelineRunRecord) {
  return run.appName && run.runType === SqlPipelineRunType.InteractivePipelineRun
    ? SqlPipelineRunType.InteractiveAppRun
    : run.runType;
}

export function getRunLogsUrl(run: PipelineRunRecord) {
  const isApp = run.runType === SqlPipelineRunType.InteractiveAppRun || run.runType === SqlPipelineRunType.AppSchedule;
  if (!run.hasRuntimeLogs || isApp || isRunInFlight(run.runStatus)) return undefined;

  return getSqlIDEUrl({
    uid: run.projectId,
    entity: Entity.Pipeline,
    name: run.pipelineName,
    runId: run.runId,
    runType:
      run.runType === SqlPipelineRunType.InteractivePipelineRun
        ? 'pipelineRun'
        : run.runType === SqlPipelineRunType.InteractiveAppRun
          ? 'appRun'
          : undefined
  });
}
